import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  CheckCircle, Printer, Scissors, Layers, BookOpen, 
  Package, ClipboardCheck, Sparkles, FileText
} from 'lucide-react'
import { dashboardService } from '../services/dashboardService'

const QueuePage = () => {
  const [queueData, setQueueData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadQueue()
  }, [])

  const loadQueue = async () => {
    try {
      const response = await dashboardService.getQueueStats()
      if (response.success) {
        setQueueData(response.data)
      }
    } catch (error) {
      console.error('Error loading queue:', error)
    } finally {
      setLoading(false)
    }
  }

  // Station configurations for each track
  const jalurIsi = [
    { id: 'validasi', name: 'Validasi File', icon: CheckCircle, color: 'bg-green-500' },
    { id: 'cetak_isi', name: 'Cetak Isi', icon: Printer, color: 'bg-blue-500' },
    { id: 'potong_isi', name: 'Potong Isi', icon: Scissors, color: 'bg-pink-500' },
    { id: 'susun', name: 'Susun', icon: Layers, color: 'bg-yellow-500' },
  ]

  const jalurCover = [
    { id: 'cetak_cover', name: 'Cetak Cover', icon: Printer, color: 'bg-red-500' },
    { id: 'qc_cover', name: 'QC Cover', icon: ClipboardCheck, color: 'bg-orange-500' },
    { id: 'laminasi', name: 'Laminasi', icon: Sparkles, color: 'bg-purple-500' },
  ]

  const jalurUtama = [
    { id: 'binding', name: 'Binding', icon: BookOpen, color: 'bg-indigo-500' },
    { id: 'potong_sisir', name: 'Potong Sisir', icon: Scissors, color: 'bg-pink-500' },
    { id: 'qc_potong', name: 'QC Potong', icon: ClipboardCheck, color: 'bg-teal-500' },
    { id: 'gudang', name: 'Gudang', icon: Package, color: 'bg-amber-500' },
  ]

  // Get count and items for a station
  const getStationData = (stationId) => {
    if (!queueData?.stations) {
      return { count: 0, items: [] }
    }
    const station = queueData.stations[stationId]
    return station || { count: 0, items: [] }
  }

  // Station Card Component
  const StationCard = ({ station }) => {
    const data = getStationData(station.id)
    const Icon = station.icon

    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className={`${station.color} px-4 py-3 flex items-center justify-between`}>
          <div className="flex items-center gap-2 text-white">
            <Icon className="w-5 h-5" />
            <span className="font-medium">{station.name}</span>
          </div>
          <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-bold">
            {data.count}
          </span>
        </div>
        
        {data.items && data.items.length > 0 ? (
          <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
            {data.items.map((item, index) => (
              <Link
                key={index}
                to={`/spk/${item.id}`}
                className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="font-medium text-gray-800 text-sm">{item.judul}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {item.qty} eks • {item.priority || 'normal'}
                  {item.schoolName && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded ${
                      item.schoolName === 'Reguler' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {item.schoolName}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center text-gray-400 text-sm">
            Tidak ada antrian
          </div>
        )}
      </div>
    )
  }

  // Track Column Component
  const TrackColumn = ({ title, icon: Icon, color, bgColor, stations }) => (
    <div className={`${bgColor} rounded-2xl p-4`}>
      <div className="flex items-center gap-2 mb-4">
        <div className={`${color} p-2 rounded-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-lg font-bold text-gray-800">{title}</h2>
      </div>
      <div className="space-y-4">
        {stations.map((station) => (
          <StationCard key={station.id} station={station} />
        ))}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="bg-purple-100 p-3 rounded-xl">
          <Layers className="w-6 h-6 text-purple-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-800">Antrian Produksi</h1>
      </div>

      {/* 3 Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Jalur ISI */}
        <TrackColumn
          title="JALUR ISI"
          icon={FileText}
          color="bg-blue-500"
          bgColor="bg-blue-50"
          stations={jalurIsi}
        />

        {/* Jalur COVER */}
        <TrackColumn
          title="JALUR COVER"
          icon={BookOpen}
          color="bg-red-500"
          bgColor="bg-red-50"
          stations={jalurCover}
        />

        {/* Jalur UTAMA */}
        <TrackColumn
          title="JALUR UTAMA"
          icon={Package}
          color="bg-purple-500"
          bgColor="bg-purple-50"
          stations={jalurUtama}
        />
      </div>
    </div>
  )
}

export default QueuePage
